<script>
  import { csv, json } from "d3-fetch";
  import { stratify } from "d3-hierarchy";
  import { pyramidSort, cactulate } from "./utils.js";
  import { line, curveBundle } from "d3-shape";
  import { scaleLinear } from "d3-scale";

  import UI from "./UI.svelte";

  let data = null;
  $: csv("/cactus/api/voelwoorden.csv").then((rawData) => {
    data = rawData;
  });

  let syns = null;
  json("/results/api/synonyms.json").then((rawData) => {
    syns = rawData
  });
  
  let scores = null
  json("/results/api/scores.json").then((rawScores) => {
    scores = rawScores.map(score => ({
      geluid: score.geluid,
      gewicht: +score.gewicht,
      materie: +score.materie,
      textuur: +score.textuur,
      vorm: +score.vorm,
      temperatuur: + score.temperatuur,
      humiditeit: +score.humiditeit,
      licht: 10 - score.gewicht,
      zwaar: +score.gewicht,
      zacht: 10 - score.materie,
      hard: +score.materie,
      glad: 10 - score.textuur,
      oneffen: +score.textuur,
      rond: 10 - score.vorm,
      scherp: +score.vorm,
      koud: 10 - score.temperatuur,
      warm: +score.temperatuur,
      droog: 10 - score.humiditeit,
      nat: +score.humiditeit
    }))
  })

  let geluidenUrls = null;
  json("/results/api/sounds.json").then(rawSounds => {
    geluidenUrls = rawSounds
  })

  const geluiden = [...Array(40).keys()].map((d) => d + 1);
  let selectedGeluid = 1;

  let selectedGeluidUrl = _ => ''
  $: if (geluidenUrls) {
    selectedGeluidUrl = selected => selected ? geluidenUrls[selected] : ''
  }

  let selectedScores = null;
  $: if (scores)
    selectedScores = scores.find((d) => d.geluid == selectedGeluid);

  let toRemove = null;
  $: if (selectedScores) {
    toRemove = [];
    Object.entries(selectedScores).forEach((d) => {
      if(+d[1] < toRemoveThreshold){
        toRemove.push(d[0])
      }
    });
  }

  let selectedSyns = null;
  $: if (scores && syns && syns.find((d) => d.key == selectedGeluid)) {
    selectedSyns = syns
      .find((d) => d.key == selectedGeluid).values
  } else {
    selectedSyns = [];
  }

  let toRemoveThreshold = 3;
  let maxNerfWidth = 4;
  let x = 495;
  let y = 400;
  let maxR = 226;
  let rootR = 100;
  let lineRootR = 4;
  let rootRotation = 5.86; //Math.round((Math.PI / 2) * 100) / 100;
  let scaleFactor = 0.95; // was 1.03
  let overlapFactor = 0.99;
  let extraAngle = 1.55; //Math.round((Math.PI / 4) * 100) / 100;
  let branchAngle = 0.34;
  let leafAngle = 0.33; //Math.round((Math.PI / 4) * 100) / 100;
  let leafR = 3;

  $: cactusOptions = {
    maxR: maxR,
    rootR: rootR,
    lineRootR: lineRootR,
    rootRotation: rootRotation,
    leafR: leafR,
    scaleFactor: scaleFactor,
    overlapFactor: overlapFactor,
    extraAngle: extraAngle,
    branchAngle: branchAngle,
    leafAngle: leafAngle,
  };

  let prunedData = null
  $: if(data && toRemove) {
    prunedData = data.filter(d => {
     return !(toRemove.includes(d.to) || toRemove.includes(d.from))
    })   
  }

  let cactusData = null
  $: if(prunedData) cactusData = stratify()
      .id((d) => d.to)
      .parentId((d) => d.from)(prunedData)
      .count().each(function (node) {
      if (node.children) {
        node.children = pyramidSort(node.children);
        let cumvalue = 0;
        node.children.forEach(function (childnode, index) {
          cumvalue = cumvalue + childnode.value;
          childnode.cumvalue = (cumvalue - childnode.value / 2) / node.value;
        });
      }
    });

  let cactusTree;
  $: cactusData && cactusOptions
    ? (cactusTree = cactulate(cactusData, cactusOptions))
    : null;

  let leafSize = 2.5;
  let leafOpacity = 0;
  let branchOpacity = 0;
  //let lineOpacity = 0.5;
  let curveBeta = 0.67;
  let lineWidth = 0.37;
  let showBranchLabels = true;
  let showLeafLabels = false;
  let contextColor = "#555555";
  let highlightColor = "#bc1cd9";

  $: lineGenerator = line()
    .x((d) => d.x)
    .y((d) => d.y)
    .curve(curveBundle.beta(curveBeta));

  $: opacityScale = scaleLinear().domain([toRemoveThreshold, 7]).range([0, 1]);
  $: widthScale = scaleLinear().domain([0,5]).range([0.5,maxNerfWidth])

  let player;
  let paused = true;

</script>

<div class="app-container">
  <!--UI
    bind:toRemoveThreshold
    bind:maxNerfWidth
    bind:x
    bind:y
    bind:maxR
    bind:rootR
    bind:lineRootR
    bind:rootRotation
    bind:scaleFactor
    bind:overlapFactor
    bind:extraAngle
    bind:branchAngle
    bind:leafAngle
    bind:leafSize
    bind:leafOpacity
    bind:branchOpacity
    bind:lineWidth
    bind:curveBeta
    bind:showBranchLabels
    bind:showLeafLabels
    bind:contextColor
    bind:highlightColor
  /-->
  <div class="relative text-md">
    <div class="absolute top-0 right-0">
      <label class="visually-hidden" for="sounds">Kies een geluid:</label>
      <select
        bind:value={selectedGeluid}
        id="sounds"
        on:change={() => {paused = true}}
        class="rounded-full pr-8"
      >
        {#each geluiden as geluid}
          <option value={geluid}>
            Geluid {geluid}
          </option>
        {/each}
      </select>
    </div>
    <audio
      bind:this={player}
      src={selectedGeluidUrl(selectedGeluid)}
      preload
      bind:paused
    />
    <svg viewBox="0 0 960 800" class="w-full h-full">
      <g transform={`translate(${x},${y})`}>
        {#if cactusTree}
          {#each cactusTree.nodes.branches as node}
            <circle
              cx={node.x}
              cy={node.y}
              r={node.r}
              fill={"white"}
              opacity={branchOpacity}
            />
          {/each}
          {#each cactusTree.nodes.branches as node}
            <circle
              cx={node.x}
              cy={node.y}
              r={node.r}
              fill={"white"}
              opacity={branchOpacity}
            />
          {/each}
          {#if selectedScores}
            {#each cactusTree.links as link}
              <path
                d={lineGenerator(link.path)}
                fill={"none"}
                stroke-width={lineWidth}
                opacity={opacityScale(+selectedScores[link.parent])}
                class="stroke-theme"
              />
            {/each}
          {/if}
          {#each cactusTree.links as link}
            {#if selectedSyns.map(d => d.synoniem).includes(link.id)}
              <path
                d={lineGenerator(link.path)}
                class="stroke-theme-inverted"
                fill={"none"}
                stroke-width={lineWidth + 2}
                opacity={1}
              />
              <path
                d={lineGenerator(link.path)}
                fill={"none"}
                stroke-width={widthScale(+selectedSyns.find(d => d.synoniem == link.id).aantal)}
                opacity={1}
                class="stroke-focus"
              />
            {/if}
          {/each}
          {#if selectedScores}
            {#each cactusTree.nodes.leaves as node}
              <circle
                cx={node.x}
                cy={node.y}
                r={leafSize}
                opacity={opacityScale(+selectedScores[node.parent])}
                stroke-width={1.5}
                class="stroke-theme fill-theme-inverted"
              />
              {#if selectedSyns.map(d => d.synoniem).includes(node.id)}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={leafSize  + widthScale(+selectedSyns.find(d => d.synoniem == node.id).aantal)}
                  opacity={1}
                  stroke-width={1.5}
                  class="stroke-theme-inverted fill-focus"
                />
              {/if}
            {/each}
          {/if}
          {#if showBranchLabels}
            {#each cactusTree.nodes.branches.filter((d) => d.id != "root") as node}
              {#if selectedScores.hasOwnProperty(node.id) && selectedScores}
                <text
                  x={node.x}
                  y={node.y}
                  text-anchor={"middle"}
                  class={"branch-label"}
                  opacity={opacityScale(+selectedScores[node.id])}>{node.id}</text
                >
              {/if}
            {/each}
          {/if}
          {#each cactusTree.nodes.leaves as node}
            {#if showLeafLabels}
              <text
                x={node.x + 6}
                y={node.y + 4}
                class="leaf-label fill-theme-inverted"
                transform={`rotate(${(-node.rotation * 180) / Math.PI},${
                  node.x
                },${node.y})`}
              >
                {node.id}
              </text>
            {/if}
            {#if selectedSyns.map(d => d.synoniem).includes(node.id)}
              <text
                x={(-node.rotation * 180) / Math.PI % 360 > -270 && (-node.rotation * 180) / Math.PI % 360 < -90
                  ? node.x -6
                  : node.x + 6}
                y={node.y + 4}
                transform={`rotate(${(-node.rotation * 180) / Math.PI % 360 > -270 && (-node.rotation * 180) / Math.PI % 360 < -90
                  ? (-node.rotation * 180) / Math.PI % 360 + 180
                  : (-node.rotation * 180) / Math.PI % 360},${
                  node.x
                },${node.y})`}
                class={(-node.rotation * 180) / Math.PI % 360 > -270 && (-node.rotation * 180) / Math.PI % 360 < -90
                  ? "leaf-label fill-focus rotated"
                  : "leaf-label fill-focus"}
              >
                {node.id}
              </text>
            {/if}
          {/each}
        {/if}
        <circle
          cx={0}
          cy={0}
          r={32}
          stroke-width={1.5}
          class="play-button stroke-theme fill-theme-inverted"
          on:click={() => {
            if (paused) {
              player.play();
            }
            if (!paused) {
              player.pause();
            }
          }}
        />
        {#if !paused}
          <rect
            x={-14}
            y={-15}
            width={10}
            height={30}
            class="pointer-events-none fill-theme"
          />
          <rect
            x={2}
            y={-15}
            width={10}
            height={30}
            class="pointer-events-none fill-theme"
          />
        {/if}
        {#if paused}
          <polygon
            points="-10,-14 16,0 -10,14"
            stroke-width={5}
            class="pointer-events-none fill-theme stroke-theme"
          />
        {/if}
      </g>
    </svg>
  </div>
</div>

<style>
  .branch-label {
    stroke-width: 3px;
    stroke: var(--theme-bg-color);
    fill: var(--theme-text-color);
    paint-order: stroke;
    stroke-linejoin: round;
    text-transform: uppercase;
    font-size: 0.8em;
  }
  .leaf-label {
    stroke-width: 3px;
    stroke: var(--theme-bg-color);
    paint-order: stroke;
    stroke-linejoin: round;
    text-anchor: start;
  }
  .leaf-label.rotated {
    text-anchor: end
  }
  audio {
    position: relative;
    top: 50px;
  }
</style>
